import React, { useEffect, useState } from 'react';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  Card,
  CardContent,
  Box,
  Button,
  Typography,
  TableContainer,
  InputAdornment,
  TextField,
  CircularProgress,
  Chip,
  Stack,
  Badge,
  TablePagination,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CategoryIcon from '@mui/icons-material/Category';
import DescriptionIcon from '@mui/icons-material/Description';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import { request } from '../../../../../utils/request';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDebounce } from '../../../../../utils/useDebounce';
import Brandcrumb from '../../../../../componentsUiBlock/Brandcrumb';

import DeleteConfirmDialog from '../../../../../componentsUiBlock/DeleteConfirmDialog';
import { useDeleteDialog } from '../../../../../utils/useDeleteDialog';
import { Category } from '../../../types';

interface HeadCell {
  id: keyof Category | 'action';
  label: string;
}

const headCells: HeadCell[] = [
  { id: 'name', label: 'Category Name' },
  { id: 'description', label: 'Description' },
  { id: 'attributesTemplate', label: 'Attributes' },
  { id: 'action', label: 'Actions' }
];

const CategoryList: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<keyof Category>('name');
  const [search, setSearch] = useState('');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const navigate = useNavigate();
  const debouncedSearch = useDebounce(search, 500);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({
          page: (page + 1).toString(),
          limit: rowsPerPage.toString(),
        });

        if (debouncedSearch) {
          params.append('q', debouncedSearch);
        }

        const url = `category/get-all-category?${params.toString()}`;

        const res = await request({
          url: url,
          method: "get",
        });
        if (res.status === "success") {
          setCategories(res.categories || []);
          setTotal(res.total)
        }
      } catch (error) {
        console.error(error);
        toast.error('Failed to load categories');
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, [debouncedSearch, page, rowsPerPage]);

  const handleSort = (property: HeadCell['id']) => {
    if (property === 'action') return;

    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property as keyof Category);
  };

  const sortedCategories = [...categories].sort((a, b) => {
    const aValue = a[orderBy];
    const bValue = b[orderBy];

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return order === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    return 0;
  });

  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const deleteCategory = async (category: Category) => {
    try {
      const res = await request({
        url: "category/delete-category",
        method: "delete",
        data: {
          id: category._id
        },
      });

      if (res?.status === "success") {
        toast.success(res?.message);
        setCategories((prev) => prev.filter((c) => c._id !== category._id));
        // If we deleted the last item on the page, go to previous page
        if (categories.length === 1 && page > 0) {
          setPage(page - 1);
        }
      } else {
        toast.error(res?.message || 'Failed to delete category');
      }
    } catch (error: any) {
      toast.error(error)
    }

  }

  const { open, loadingDelete, itemToDelete, openDialog, closeDialog, confirmDelete } = useDeleteDialog<Category>({
    onDelete: deleteCategory
  })

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
        <Brandcrumb />
      </Box>
      <Card elevation={4}>
        <CardContent sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Button
              variant="contained"
              size='small'
              color="primary"
              onClick={() => navigate("/configuration/categories/addCategory")}
              sx={{ flexShrink: 0 }}
              startIcon={<CategoryIcon />}
            >
              Add New Category
            </Button>

            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={3}>
              {
                <Badge badgeContent={total} showZero color="primary">
                  <CategoryIcon color="action" />
                </Badge>
              }
              <TextField
                size="small"
                placeholder="Search by category name, alias, or description..."
                variant="outlined"
                sx={{ width: { xs: '100%', sm: 300 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => setSearch(e.target.value)}
              />

            </Box>

          </Box>
        </CardContent>

        <CardContent sx={{ pt: 1 }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {headCells.map((head) => (
                    <TableCell key={head.id} sx={{ fontWeight: 'bold' }}>
                      {head.id !== 'action' ? (
                        <TableSortLabel
                          active={orderBy === head.id}
                          direction={orderBy === head.id ? order : 'asc'}
                          onClick={() => handleSort(head.id)}
                        >
                          {head.label}
                        </TableSortLabel>
                      ) : (
                        head.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {debouncedSearch ? 'Searching...' : 'Loading categories...'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : sortedCategories.length > 0 ? (
                  sortedCategories.map((category) => (
                    <TableRow key={category._id} hover>
                      {/* Category Name with Alias Names */}
                      <TableCell>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <CategoryIcon sx={{ color: 'black', fontSize: 'small' }} />
                            <Typography variant="body2" fontWeight={500}>
                              {category.name}
                            </Typography>
                          </Box>

                          {/* Alias Names Display */}
                          {category.aliasNames && category.aliasNames.length > 0 && (
                            <Box display="flex" alignItems="flex-start" gap={0.5} sx={{ ml: 3 }}>
                              <AlternateEmailIcon
                                sx={{
                                  color: 'text.secondary',
                                  fontSize: '0.75rem',
                                  mt: 0.25
                                }}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  fontStyle: 'italic',
                                  lineHeight: 1.2
                                }}
                              >
                                {category.aliasNames.join(', ')}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </TableCell>

                      {/* Description */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <DescriptionIcon sx={{ color: 'black', fontSize: 'small' }} />
                          <Typography variant="body2" sx={{
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {category.description || 'No description'}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Attributes Template */}
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {category.attributesTemplate && category.attributesTemplate.length > 0 ? (
                            <>
                              {category.attributesTemplate.slice(0, 3).map((attribute, index) => (
                                <Chip
                                  key={attribute._id}
                                  label={attribute.label}
                                  size="small"
                                  sx={{
                                    fontSize: '0.7rem',
                                    height: 24,
                                    mb: 0.5,
                                    color: 'black',
                                    borderColor: 'black',
                                    backgroundColor: 'transparent',
                                    '& .MuiChip-label': {
                                      px: 1,
                                    },
                                    '&:hover': {
                                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    }
                                  }}
                                  variant="outlined"
                                />
                              ))}
                              {category.attributesTemplate.length > 3 && (
                                <Chip
                                  label={`+${category.attributesTemplate.length - 3}`}
                                  size="small"
                                  sx={{
                                    fontSize: '0.7rem',
                                    height: 24,
                                    mb: 0.5,
                                    color: 'black',
                                    borderColor: 'black',
                                    backgroundColor: 'transparent',
                                    '&:hover': {
                                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    }
                                  }}
                                  variant="outlined"
                                />
                              )}
                            </>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              No attributes
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>

                      {/* Action Buttons */}
                      <TableCell>

                        <Box display="flex" flexDirection="column" gap={0.8}>
                          <Button
                            variant="text"
                            onClick={() => navigate(`/configuration/categories/${category._id}`)}
                            sx={{
                              minWidth: 'auto',
                              fontSize: '0.60rem',
                              py: 0.1,
                              textWrap: 'nowrap',
                              px: 2,
                              borderRadius: 5,
                              border: '.8px solid #7f5ee666',
                              '&:hover': { backgroundColor: '#7f5ee666' }
                            }}
                          >
                            View / Edit
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            color="error"
                            onClick={() => openDialog(category)}
                            sx={{
                              minWidth: 'auto',
                              fontSize: '0.60rem',
                              py: 0.1,
                              px: 2,
                              borderRadius: 5,
                              border: '.8px solid #ff4d6e5e',
                              '&:hover': { backgroundColor: '#ff4d6e5e' }
                            }}
                          >
                            Delete
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <CategoryIcon sx={{ fontSize: 48, color: 'black', mb: 2 }} />
                        <Typography variant="body1" color="text.secondary" gutterBottom>
                          {search ? `No categories found for "${search}"` : 'No categories available'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {search ? 'Try adjusting your search terms' : 'Get started by adding your first category'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Component - Added at the bottom */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          />
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}

      <DeleteConfirmDialog
        open={open}
        loading={loadingDelete}
        title="Delete Category"
        description={
          <>
            Are you sure you want to delete the category <strong>"{itemToDelete?.name}"</strong>?
            {itemToDelete?.aliasNames && itemToDelete.aliasNames.length > 0 && (
              <> Also including aliases: <strong>{itemToDelete.aliasNames.join(', ')}</strong></>
            )} <br /><br />
            <Alert severity="warning" sx={{ fontSize: "12px", py: "0px" }}>This action cannot be undone and all associated data will be permanently removed.</Alert>
          </>
        }
        onCancel={closeDialog}
        onConfirm={confirmDelete}
      />
    </>
  );
};

export default CategoryList;